import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Card, CardBody, CardHeader, CardTitle, Col, Media, Row, Spinner, Table } from "reactstrap";
import Button from "react-bootstrap/Button";
import { Line } from "react-chartjs-2";
import { AlertTriangle, Globe, Lock, Shield } from "react-feather";

import { apiFetch, API } from "../../services/api";
import BlockIpModal from "../../components/security/BlockIpModal";
import {
    formatCount,
    formatDateTime,
    sortTop,
    seriesToChart,
    alertTypeLabel,
    SECURITY_PERIODS,
} from "../../helpers/security";

const REFRESH_MS = 60000;

// Compteur : une tuile par indicateur. `value` peut manquer (le back n'a pas
// encore la source) — on affiche « — » plutôt qu'un zéro qui ferait croire au
// calme plat.
function Counter({ icon: Icon, tone, label, value, hint, loading }) {
    return (
        <Col md="3">
            <Card className="flex-fill">
                <CardBody className="py-4">
                    <Media>
                        <div className="d-inline-block mt-2 mr-3">
                            <Icon className={"feather-lg text-" + tone} />
                        </div>
                        {loading ? (
                            <Media body>
                                <div className="mb-0">
                                    <Spinner size="sm" />
                                </div>
                            </Media>
                        ) : (
                            <Media body>
                                <h3 className="mb-2">{formatCount(value)}</h3>
                                <div className="mb-0">{label}</div>
                                {hint ? <div className="text-muted small">{hint}</div> : null}
                            </Media>
                        )}
                    </Media>
                </CardBody>
            </Card>
        </Col>
    );
}

// Vue d'ensemble « Sécurité » : ce qui se passe sur le périmètre (WAF, APIs,
// connexions au back-office) sur 24 h / 7 j / 30 j. Tout vient des agrégats du
// robot `security-watch` (`security_events`), jamais des journaux bruts.
export function SecurityOverview() {
    const [period, setPeriod] = useState("24h");
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [refreshedAt, setRefreshedAt] = useState(null);
    const [blockTarget, setBlockTarget] = useState(null);
    // IP bloquées pendant la session : le prochain rafraîchissement (60 s) les
    // remontera avec `blocked:true`, en attendant on l'affiche tout de suite.
    const [justBlocked, setJustBlocked] = useState([]);

    const cancelled = useRef(false);

    const load = useCallback(
        (selected) => {
            apiFetch(API.back + "/security/overview?period=" + encodeURIComponent(selected), {
                method: "GET",
                market: false,
            })
                .then((res) => {
                    if (!res.ok) throw new Error("HTTP " + res.status);
                    return res.json();
                })
                .then((payload) => {
                    if (cancelled.current) return;
                    setData(payload && typeof payload === "object" ? payload : {});
                    setError(null);
                    setRefreshedAt(new Date());
                })
                .catch((err) => {
                    console.log(err);
                    if (cancelled.current) return;
                    setError("Vue d'ensemble indisponible (" + err.message + ") — vérifier la connexion au back-office.");
                    setData((current) => current || {});
                });
        },
        []
    );

    useEffect(() => {
        cancelled.current = false;
        setData(null);
        load(period);
        // Rafraîchissement automatique : la page sert à surveiller, elle doit
        // rester juste sans intervention.
        const timer = setInterval(() => load(period), REFRESH_MS);
        return () => {
            cancelled.current = true;
            clearInterval(timer);
        };
    }, [period, load]);

    const loading = data === null && !error;
    const overview = data || {};
    const totals = overview.totals || {};
    const findings = Array.isArray(overview.findings) ? overview.findings : [];
    const topIps = sortTop(overview.topIps, "count").slice(0, 10);
    const topPaths = sortTop(overview.topPaths, "count").slice(0, 10);
    const topRules = sortTop(overview.topRules, "count").slice(0, 10);
    const chart = seriesToChart(overview.series);
    const hasSeries = chart.labels.length > 0;

    const blockMode = String(overview.apisMode || "") === "block";
    const isBlocked = (row) => row.blocked === true || justBlocked.indexOf(row.ip) !== -1;

    const chartOptions = {
        maintainAspectRatio: false,
        legend: { display: true },
        tooltips: { intersect: false },
        scales: {
            xAxes: [{ gridLines: { color: "rgba(0,0,0,0.05)" }, ticks: { maxTicksLimit: 16 } }],
            yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
        },
    };

    return (
        <div>
            <Card className="flex-fill w-100">
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <CardTitle tag="h5" className="mb-1">
                                Sécurité — vue d'ensemble
                            </CardTitle>
                            <div className="text-muted small">
                                <Badge color={blockMode ? "success" : "warning"} className="mr-2">
                                    WAF APIs : mode {blockMode ? "bloquer" : "compter"}
                                </Badge>
                                {blockMode
                                    ? "Les règles managées refusent le trafic."
                                    : "Les règles managées comptent sans refuser — on observe avant de bloquer."}
                                {refreshedAt ? (
                                    <span className="ml-2">Actualisé à {refreshedAt.toLocaleTimeString("fr-FR")}.</span>
                                ) : null}
                            </div>
                        </div>
                        <div>
                            {SECURITY_PERIODS.map((p) => (
                                <Button
                                    key={p.value}
                                    size="sm"
                                    className="ml-1"
                                    variant={period === p.value ? "primary" : "outline-secondary"}
                                    onClick={() => setPeriod(p.value)}
                                >
                                    {p.label}
                                </Button>
                            ))}
                            <Link to="/security/journal" className="btn btn-outline-secondary btn-sm ml-2">
                                Journal
                            </Link>
                        </div>
                    </div>
                </CardHeader>
                <CardBody>
                    {error ? <div className="alert alert-danger">{error}</div> : null}

                    {findings.length > 0 ? (
                        <div className="alert alert-warning">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <strong>
                                        {findings.length === 1
                                            ? "1 constat ouvert sur le compte AWS"
                                            : findings.length + " constats ouverts sur le compte AWS"}
                                    </strong>
                                    <ul className="mb-0 mt-2 pl-3">
                                        {findings.slice(0, 5).map((finding, index) => (
                                            <li key={(finding.alertKey || "") + index}>
                                                <span className="font-weight-bold">{alertTypeLabel(finding.type)}</span>
                                                {" — "}
                                                {finding.title || "(sans titre)"}
                                                <span className="text-muted"> · {formatDateTime(finding.sentAt)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    {findings.length > 5 ? (
                                        <div className="small mt-1">
                                            et {findings.length - 5} autre(s) — tout est dans le journal.
                                        </div>
                                    ) : null}
                                </div>
                                <Link to="/security/journal" className="btn btn-warning btn-sm ml-3 flex-shrink-0">
                                    Ouvrir le journal
                                </Link>
                            </div>
                        </div>
                    ) : null}

                    <Row>
                        <Counter
                            icon={Shield}
                            tone="danger"
                            label="Requêtes bloquées"
                            value={totals.wafBlocked}
                            hint={
                                totals.wafCounted === undefined || totals.wafCounted === null
                                    ? null
                                    : formatCount(totals.wafCounted) + " comptées (non bloquées)"
                            }
                            loading={loading}
                        />
                        <Counter
                            icon={Globe}
                            tone="warning"
                            label="IP hostiles"
                            value={totals.hostileIps}
                            hint="Adresses vues sur la période"
                            loading={loading}
                        />
                        <Counter
                            icon={AlertTriangle}
                            tone="primary"
                            label="Erreurs API"
                            value={totals.apiErrors}
                            hint="Réponses 4xx / 5xx"
                            loading={loading}
                        />
                        <Counter
                            icon={Lock}
                            tone="info"
                            label="Échecs de connexion"
                            value={totals.loginFails}
                            hint="Back-office"
                            loading={loading}
                        />
                    </Row>

                    {loading ? (
                        <div className="d-flex align-items-center">
                            <Spinner color="primary" size="sm" />
                            <span className="text-muted ml-2">Chargement…</span>
                        </div>
                    ) : null}

                    {!loading ? (
                        <Card className="mb-0">
                            <CardHeader className="pb-0">
                                <CardTitle tag="h6" className="mb-0">
                                    Activité sur la période
                                </CardTitle>
                            </CardHeader>
                            <CardBody>
                                {hasSeries ? (
                                    <div style={{ height: 280 }}>
                                        <Line data={chart} options={chartOptions} />
                                    </div>
                                ) : (
                                    <p className="text-muted mb-0">Aucune donnée agrégée sur la période.</p>
                                )}
                            </CardBody>
                        </Card>
                    ) : null}
                </CardBody>
            </Card>

            <Row>
                <Col lg="6">
                    <Card className="flex-fill w-100">
                        <CardHeader>
                            <CardTitle tag="h5" className="mb-0">
                                Top IP
                            </CardTitle>
                            <div className="text-muted small mt-1">
                                Adresses les plus vues par le WAF. « Bloquer » coupe tout le trafic de l'adresse.
                            </div>
                        </CardHeader>
                        <CardBody>
                            {topIps.length === 0 ? (
                                <p className="text-muted mb-0">Aucune IP remarquable sur la période.</p>
                            ) : (
                                <div style={{ overflowX: "auto" }}>
                                    <Table responsive size="sm" className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>IP</th>
                                                <th>Pays</th>
                                                <th>Requêtes</th>
                                                <th>Règle dominante</th>
                                                <th>Dernière vue</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {topIps.map((row) => (
                                                <tr key={row.ip}>
                                                    <td>{row.ip || "—"}</td>
                                                    <td>{row.country || "—"}</td>
                                                    <td>{formatCount(row.count)}</td>
                                                    <td className="text-muted small">{row.rule || "—"}</td>
                                                    <td className="text-muted small">{formatDateTime(row.lastSeen)}</td>
                                                    <td className="table-action">
                                                        {isBlocked(row) ? (
                                                            <Badge color="danger">Bloquée</Badge>
                                                        ) : (
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={() => setBlockTarget(row.ip)}
                                                            >
                                                                Bloquer
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>

                <Col lg="3">
                    <Card className="flex-fill w-100">
                        <CardHeader>
                            <CardTitle tag="h5" className="mb-0">
                                Top chemins visés
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            {topPaths.length === 0 ? (
                                <p className="text-muted mb-0">Aucun chemin remarquable.</p>
                            ) : (
                                <Table responsive size="sm" className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Chemin</th>
                                            <th>Requêtes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topPaths.map((row, index) => (
                                            <tr key={(row.path || "") + index}>
                                                <td style={{ wordBreak: "break-all" }}>{row.path || "—"}</td>
                                                <td>{formatCount(row.count)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                </Col>

                <Col lg="3">
                    <Card className="flex-fill w-100">
                        <CardHeader>
                            <CardTitle tag="h5" className="mb-0">
                                Top règles
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            {topRules.length === 0 ? (
                                <p className="text-muted mb-0">Aucune règle déclenchée.</p>
                            ) : (
                                <Table responsive size="sm" className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Règle</th>
                                            <th>Requêtes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topRules.map((row, index) => (
                                            <tr key={(row.rule || "") + index}>
                                                <td style={{ wordBreak: "break-all" }}>{row.rule || "—"}</td>
                                                <td>{formatCount(row.count)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {blockTarget !== null ? (
                <BlockIpModal
                    isOpen={true}
                    ip={blockTarget}
                    closeModal={() => setBlockTarget(null)}
                    onBlocked={(ip) => {
                        setJustBlocked((current) => current.concat([ip]));
                        setBlockTarget(null);
                        load(period);
                    }}
                />
            ) : null}
        </div>
    );
}

export default SecurityOverview;
